import {
	Box,
	Button,
	Card,
	Chip,
	Divider,
	Grid,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import useModal from '@/common/components/ConfirmationDialog/useModal';

import { useNavigate, useParams } from 'react-router-dom';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import AssignmentReturnedOutlinedIcon from '@mui/icons-material/AssignmentReturnedOutlined';
import { AwardType, OnoffType } from '@/api/enum/common.enum';
import { showLabel } from '@/utils/helper';
import SingleInfoForm from './components/SingleInfoForm';
import {
	useDeleteAchievementTreasureSingleMutation,
	useGetAchievementTreasureSingleDetailQuery,
	useSetAchievementTreasureSingleStatusMutation,
} from '@/api/task/treasureApiSlice';
import PublishedChip from '@/common/components/PublishedChip';
import { PUBLISHED_LISTB } from '@/common/constants/common';
import WarningIcon from '@mui/icons-material/Warning';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function TreasureSingleDetailPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const showModal = useModal();
	const { treasureId, treasureSingleId } = useParams();
	const navigate = useNavigate();
	const [deleteAchievementTreasureSingle] =
		useDeleteAchievementTreasureSingleMutation();
	const [setAchievementTreasureSingleStatus] =
		useSetAchievementTreasureSingleStatusMutation();

	const awardTypeList = [
		{ id: AwardType.Int, name: t('taskManage.Int') },
		{ id: AwardType.Prop, name: t('taskManage.Prop') },
		{ id: AwardType.IntProp, name: t('taskManage.IntProp') },
	];

	const { data: details } = useGetAchievementTreasureSingleDetailQuery(
		treasureSingleId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const handleDelete = async () => {
		if (details?.data.published == OnoffType.on) {
			return enqueueSnackbar(t('snackbar.noDeleteMeditation'), {
				variant: 'error',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('achievementList.deleteTreasureSingle')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('achievementList.isDeleteTreasureSingle')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteAchievementTreasureSingle(
						treasureSingleId || ''
					);
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						navigate(
							`/task-manage/achievement-list/treasure/info/${treasureId}`,
							{
								replace: true,
							}
						);
					}
				}}
			/>
		));
	};

	const handleSetStatus = async (type: number) => {
		const response = await setAchievementTreasureSingleStatus({
			publishList: [
				{
					id: treasureSingleId || '',
					published: type == 1 ? true : false,
				},
			],
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.changeStatusSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.treasurSingleDetail')}
					</Typography>
					<Stack direction={'row'} alignItems={'center'}>
						{details?.data.published == OnoffType.on && (
							<Button
								endIcon={<AssignmentReturnedOutlinedIcon />}
								onClick={() => handleSetStatus(0)}
								sx={{ marginRight: '24px' }}
								variant='contained'
							>
								{t('meditation.handleOff')}
							</Button>
						)}
						{details?.data.published == OnoffType.off && (
							<Button
								endIcon={<AssignmentReturnedOutlinedIcon />}
								onClick={() => handleSetStatus(1)}
								sx={{ marginRight: '24px' }}
								variant='contained'
							>
								{t('meditation.handleOn')}
							</Button>
						)}
						<Button
							endIcon={<DeleteOutlined />}
							onClick={() => handleDelete()}
							sx={{ marginRight: '24px' }}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Stack>
				</Box>

				<Card
					sx={{ padding: type == FormType.view ? '0 24px' : 0, marginTop: 3 }}
				>
					<Box sx={{ padding: type == FormType.view ? 0 : '0 24px' }}>
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ paddingTop: '24px' }}
						>
							<Typography variant='h6'>{t('meditation.record')}</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView
									label={t('achievementList.seriesStatus')}
									valueElement={
										<Box display='flex' alignItems='center'>
											<PublishedChip
												published={details?.data.published}
												publishList={PUBLISHED_LISTB}
											/>
											{details?.data.published == OnoffType.on &&
												details?.data.allRewardUnpublished && (
													<Tooltip
														title={`${t('regularTask.statusTips')}`}
														sx={{ color: '#FA9E26' }}
													>
														<WarningIcon />
													</Tooltip>
												)}
										</Box>
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView label={t('achievementList.level')} value={'1'} />
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.createTime')}
									value={
										!details?.data.createdAt
											? '-'
											: dayjs(details?.data.createdAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.lastUpdateTime')}
									value={
										!details?.data.updatedAt
											? '-'
											: dayjs(details?.data.updatedAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
						</Grid>
					</Box>

					<Divider />
					{(() => {
						switch (type) {
							case FormType.view:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ paddingTop: '24px' }}
										>
											<Typography variant='h6'>
												{t('meditation.text')}
											</Typography>
											<Button
												endIcon={<CreateOutlinedIcon />}
												color='secondary'
												onClick={() => {
													setType(FormType.edit);
												}}
											>
												{t('button.edit')}
											</Button>
										</Box>
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={6}>
												<AppFieldView
													label={t('taskManage.total')}
													value={details?.data.finishedNum}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('taskManage.awardType')}
													value={showLabel(
														details?.data.rewardType,
														awardTypeList
													)}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('taskManage.rewardPoints')}
													value={details?.data.rewardPoints}
												/>
											</Grid>
											<Grid item xs={6}></Grid>
											{details?.data.rewardList?.map((item) => (
												<>
													<Grid item xs={1}>
														<AppFieldView
															valueElement={
																<PublishedChip
																	published={item.published}
																	publishList={PUBLISHED_LISTB}
																/>
															}
														/>
													</Grid>
													<Grid item xs={1}>
														<AppFieldView
															label={t('regularTask.rewardItem')}
															value={item.seriesTitleZh + ' - ' + item.titleZh}
														/>
													</Grid>
													<Grid item xs={10}>
														<AppFieldView
															label={t('regularTask.rewardItemCount')}
															value={item.rewardNum}
														/>
													</Grid>
												</>
											))}
										</Grid>
									</>
								);
							case FormType.edit:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ padding: '24px', paddingBottom: '0' }}
										>
											<Typography variant='h6'>
												{t('meditation.text')}
											</Typography>
										</Box>
										<SingleInfoForm
											type={type}
											setType={setType}
											details={details?.data}
										/>
									</>
								);
						}
					})()}
				</Card>
			</Box>
		</>
	);
}
